/**
 * The system's undo for the app's undo manager (`useUndoManager` in
 * @swiftbrowser/react, whose stack is the truth): where the system's undo
 * and redo come from, and what it is told the stack can do. In a browser
 * that is the keyboard: ⌘Z and ⇧⌘Z (Ctrl-Z, Ctrl-Shift-Z and Ctrl-Y off
 * Apple platforms) while no text input has the focus go to the manager; a
 * focused field or editor keeps the browser's own undo of what was typed in
 * it. A page has no Edit menu, so `update` has nothing to show. The native
 * app replaces this host with one over the window's `UndoManager`
 * (packages/native/src/undo.ts), which the shake, the three-finger swipe,
 * the keyboard and the Edit menu reach.
 *
 * A Swift app's manager is in Wasm (the shim's `UndoManager`, one per app):
 * it reports what it can do through `undo` host requests (`performUndo`),
 * and the host's commands go back to it as `undo` and `redo` events
 * (`relayUndo`, which the page installs in the request environment).
 */

import type { UndoRequestParams } from './protocol';

/** What the stack can do, for the system to enable and name (the Edit menu's "Undo Move"). */
export interface UndoState {
  canUndo: boolean;
  canRedo: boolean;
  /** The name of what undo takes back, or '' for none. */
  undoActionName: string;
  redoActionName: string;
}

/** The manager's undo and redo, as the system calls them. */
export interface UndoCommands {
  undo(): void;
  redo(): void;
}

export interface UndoHost {
  /** The stack after a change: what the system enables and names. */
  update(state: UndoState): void;
  /**
   * Where the system's undo and redo go from now on, until the returned
   * function is called. One manager at a time: the last to listen.
   */
  listen(commands: UndoCommands): () => void;
}

/** The part of a `Document` the host listens on; tests fake it. */
export interface UndoKeyTarget {
  addEventListener(type: 'keydown', listener: (event: KeyboardEvent) => void): void;
  removeEventListener(type: 'keydown', listener: (event: KeyboardEvent) => void): void;
}

/** Whether the keyboard's command key is ⌘ (Apple platforms) rather than Ctrl. */
export const isApplePlatform = (): boolean => typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.platform || navigator.userAgent);

/** The undo or redo a key press asks for, or null for any other press. */
export function undoKey(event: Pick<KeyboardEvent, 'key' | 'metaKey' | 'ctrlKey' | 'altKey' | 'shiftKey'>, apple: boolean): 'undo' | 'redo' | null {
  if (event.altKey) return null;
  const command = apple ? event.metaKey && !event.ctrlKey : event.ctrlKey && !event.metaKey;
  if (!command) return null;
  const key = event.key.toLowerCase();
  if (key === 'z') return event.shiftKey ? 'redo' : 'undo';
  if (key === 'y' && !apple && !event.shiftKey) return 'redo';
  return null;
}

/** Whether `target` is in a text input, whose own undo the browser keeps. */
export function inTextInput(target: EventTarget | null): boolean {
  const element = target as { closest?: (selector: string) => unknown } | null;
  return typeof element?.closest === 'function' && element.closest('input, textarea, select, [contenteditable]:not([contenteditable="false"])') != null;
}

/** The page's host: the keyboard, listened to while a manager listens. */
export function browserUndoHost(target: UndoKeyTarget, apple: boolean = isApplePlatform()): UndoHost {
  let listener: ((event: KeyboardEvent) => void) | null = null;
  return {
    update: () => undefined,
    listen(commands) {
      if (listener) target.removeEventListener('keydown', listener);
      const onKey = (event: KeyboardEvent): void => {
        if (event.defaultPrevented || event.isComposing || inTextInput(event.target)) return;
        const command = undoKey(event, apple);
        if (!command) return;
        event.preventDefault();
        commands[command]();
      };
      listener = onKey;
      target.addEventListener('keydown', onKey);
      return () => {
        if (listener !== onKey) return;
        target.removeEventListener('keydown', onKey);
        listener = null;
      };
    },
  };
}

/**
 * An `undo` host request: the Swift app's manager, after a turn that changed
 * what it can do. The host shows nothing on a page, but learns the state;
 * a page without a host (no document) has nothing to tell. Answered `{}`.
 */
export function performUndo(params: UndoRequestParams, host: UndoHost | undefined): Record<string, never> {
  host?.update({ canUndo: params.canUndo, canRedo: params.canRedo, undoActionName: params.undoActionName, redoActionName: params.redoActionName });
  return {};
}

/**
 * The host as the Swift app's request environment sees it: `update` passes
 * through, and the first one (the app has a manager in use) makes the host
 * listen, its undo and redo sent to the app as `undo` and `redo` events,
 * which the runtime routes to the manager. Listening lasts: the stack the
 * keys act on is the app's, which knows when there is nothing to undo.
 */
export function relayUndo(host: UndoHost, send: (event: { type: 'undo' | 'redo' }) => void): UndoHost {
  let listening = false;
  return {
    update(state) {
      if (!listening) {
        listening = true;
        host.listen({ undo: () => send({ type: 'undo' }), redo: () => send({ type: 'redo' }) });
      }
      host.update(state);
    },
    listen: (commands) => host.listen(commands),
  };
}
